// Офлайн-чтение базы и очередь отложенных действий (M10.3, M10.4).
//
// Чтение без сети обеспечивает service worker (`web/sw-template.js`): тело
// заметки отдаётся из кеша, дерево и лента — из сети с откатом в кеш. Здесь —
// вторая половина задачи: то, что человек СДЕЛАЛ в офлайне, и что нужно
// доделать, когда сеть вернётся.
//
// Почему очередь именно из действий, а не из правок текста. Тело заметки на
// телефоне руками не правится вообще (M4.7): правка идёт голосом и через
// агента. Значит копить diff'ы markdown не нужно и нечего — в офлайне можно
// сделать ровно три вещи: отметить прочитанным, создать заметку и написать
// агенту. Их и откладываем.
//
// Ключ хранилища — НА ПРОФИЛЬ. У профилей разные базы; отложенное «отметить
// прочитанным» из одного профиля, выполненное под другим, тронуло бы чужие
// заметки. Профиль читаем из того же ключа `agentbox.profile`, который пишет
// appStore, — чтобы не заводить второй источник правды.

import { notesApi } from '../api'
import { load, save } from '../lib/persist'
import type { NoteType } from '../protocol'
import { useAppStore } from '../store/appStore'

export interface SyncState {
  online: boolean
  /** сколько правок ждёт отправки */
  queued: number
  /** когда последний раз успешно синхронизировались, unix ts (СЕКУНДЫ, как
   *  все времена в protocol.ts: `Date.now() / 1000`) */
  syncedAt: number | null
}

/** Что телефон умеет отложить до появления сети. */
export type QueuedAction =
  /** «прочитано» по карточке ленты или по всей группе (M7.1, M7.3) */
  | { kind: 'read'; paths: string[] }
  /** FAB «+» на экране базы (M3.4) */
  | { kind: 'note'; path: string; title?: string; type?: NoteType }
  /** реплика агенту из шторки или беседы (M5, M6) */
  | { kind: 'reply'; sessionId: string; text: string }

export interface QueuedItem {
  id: string
  /** когда действие сделали, unix ts в секундах */
  at: number
  action: QueuedAction
}

/** Отправщик реплик агенту. См. `registerReplySender`. */
export type ReplySender = (sessionId: string, text: string) => boolean | Promise<boolean>

/**
 * Чем кончилась попытка выполнить действие.
 *
 * `parked` — отдельный исход намеренно. «Некому доставить прямо сейчас» и «не
 * удалось доставить» выглядят одинаково (действие осталось в очереди), но
 * лечатся по-разному: второе — повтором через таймер, а первое повтором не
 * лечится вообще. Реплику для закрытой беседы доставит не время, а открытие
 * этой беседы, и будить телефон таймером раз в минуту до тех пор — чистый
 * расход батареи.
 */
type RunResult = 'done' | 'retry' | 'parked'

const QUEUE_KEY = 'mobile.queue'
const SYNC_KEY = 'mobile.synced'

/** Пауза перед повтором, если сеть формально есть, а запрос не прошёл.
 *  Растёт до минуты: долбиться в мёртвый сервер раз в секунду — греть телефон. */
const RETRY_MS = [2000, 5000, 15000, 60000]

// ---------- состояние модуля ----------

let started = false
/** Профиль, под который загружены `queue` и `syncedAt`. `undefined` — ещё не читали. */
let profile: string | null | undefined = undefined
let queue: QueuedItem[] = []
let syncedAt: number | null = null
let flushing = false
/** Элемент, который прямо сейчас уезжает: в него нельзя дописывать пути. */
let inflight: QueuedItem | null = null
let attempt = 0
let retryTimer: number | null = null

let snapshot: SyncState = { online: true, queued: 0, syncedAt: null }
const listeners = new Set<(state: SyncState) => void>()
let replySender: ReplySender | null = null

// ---------- хранилище ----------

function keyFor(base: string): string {
  return `${base}.${profile ?? 'main'}`
}

function currentProfile(): string | null {
  return load<string | null>('profile', null)
}

/** Перечитать очередь, если сменился профиль (вход под другим паролем). */
function ensureProfile(): void {
  const now = currentProfile()
  if (profile !== undefined && profile === now) return
  profile = now
  queue = load<QueuedItem[]>(keyFor(QUEUE_KEY), []).filter(isItem)
  syncedAt = load<number | null>(keyFor(SYNC_KEY), null)
  dropForeignCache(now)
}

/**
 * Кеш прочитанной базы у service worker один на origin, а базы у профилей
 * разные. Значит вход под другим профилем обязан этот кеш обнулить — иначе
 * офлайн-читалка покажет чужие заметки.
 *
 * Владельца кеша помним отдельным ключом (не на профиль!), чтобы смена
 * пережила перезагрузку: сравнить «кто в кеше» с «кто вошёл» можно только так.
 */
function dropForeignCache(now: string | null): void {
  const OWNER_KEY = 'mobile.cacheOwner'
  if (load<string | null>(OWNER_KEY, null) === now) return
  save(OWNER_KEY, now)
  navigator.serviceWorker?.controller?.postMessage('drop-notes-cache')
}

/** Хранилище мог испортить кто угодно — от старой версии панели до человека
 *  с открытой консолью. Битую запись молча выбрасываем, а не роняем экран. */
function isItem(value: unknown): value is QueuedItem {
  if (!value || typeof value !== 'object') return false
  const item = value as QueuedItem
  if (typeof item.id !== 'string' || typeof item.at !== 'number') return false
  const action = item.action
  if (!action || typeof action !== 'object') return false
  if (action.kind === 'read') return Array.isArray(action.paths)
  if (action.kind === 'note') return typeof action.path === 'string'
  if (action.kind === 'reply') {
    return typeof action.sessionId === 'string' && typeof action.text === 'string'
  }
  return false
}

function persist(): void {
  save(keyFor(QUEUE_KEY), queue)
}

function uid(): string {
  const crypto = globalThis.crypto
  if (crypto && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  return `q${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`
}

// ---------- снимок состояния ----------

/** Пересобрать снимок. Объект остаётся ТЕМ ЖЕ, пока значения не изменились:
 *  строка статуса читает состояние через `useSyncExternalStore`, а он сравнивает
 *  снимки по ссылке и уходит в бесконечный цикл, если каждый раз новый объект. */
function refresh(notify: boolean): void {
  const online = typeof navigator === 'undefined' ? true : navigator.onLine
  if (
    snapshot.online === online &&
    snapshot.queued === queue.length &&
    snapshot.syncedAt === syncedAt
  ) {
    return
  }
  snapshot = { online, queued: queue.length, syncedAt }
  if (!notify) return
  for (const cb of [...listeners]) cb(snapshot)
}

/**
 * Текущее состояние синхронизации для строки статуса базы (M3.3).
 *
 * Чистая функция без побочных эффектов наружу — её зовут прямо из рендера.
 */
export function readSyncState(): SyncState {
  start()
  ensureProfile()
  refresh(false)
  return snapshot
}

/**
 * Подписка на изменения состояния: сеть пропала, очередь выросла или
 * рассосалась, база синхронизировалась. Возвращает отписку.
 *
 * Пара к `readSyncState` — вместе они готовый аргумент `useSyncExternalStore`:
 * `useSyncExternalStore(subscribeSync, readSyncState)`. Поэтому подписка
 * СОЗНАТЕЛЬНО не зовёт колбэк сразу: текущее значение и так берётся чтением.
 */
export function subscribeSync(cb: (state: SyncState) => void): () => void {
  start()
  ensureProfile()
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

/** Отметить, что база только что успешно прочитана с сервера. */
export function markSynced(at: number = Date.now() / 1000): void {
  ensureProfile()
  syncedAt = at
  save(keyFor(SYNC_KEY), at)
  refresh(true)
}

// ---------- очередь ----------

/**
 * Отложить действие и тут же попробовать выполнить.
 *
 * Один путь и в онлайне, и в офлайне — намеренно. Ветка «если сеть есть, шлём
 * мимо очереди» выглядит экономнее, но именно она теряет данные: сеть на
 * телефоне «есть» ровно до момента отправки. Здесь действие сначала ложится в
 * localStorage и только потом уезжает, так что переживает и обрыв, и закрытие
 * вкладки.
 *
 * Оптимистичный интерфейс — забота вызывающего: очередь ничего не рисует.
 */
export function queueAction(action: QueuedAction): void {
  start()
  ensureProfile()

  // Соседние «прочитано» схлопываем: тап по трём карточкам подряд в офлайне
  // не должен показывать «правки в очереди (3)» и потом слать три запроса.
  const last = queue[queue.length - 1]
  // Но не в тот элемент, который прямо сейчас улетает: он уедет со старым
  // списком и тут же будет выброшен из очереди вместе с дописанным.
  const busy = flushing && last === inflight
  if (action.kind === 'read' && last && !busy && last.action.kind === 'read') {
    const merged = new Set([...last.action.paths, ...action.paths])
    last.action = { kind: 'read', paths: [...merged] }
  } else {
    queue.push({ id: uid(), at: Date.now() / 1000, action })
  }
  persist()
  refresh(true)
  void flushQueue()
}

/** Что лежит в очереди — для отладки и для экрана, если он захочет показать список. */
export function readQueue(): QueuedItem[] {
  start()
  ensureProfile()
  return [...queue]
}

/**
 * Реплики агенту уходят по WebSocket живого окна беседы — REST-ручки «написать
 * в сессию» на сервере нет вовсе. Очередь до сокета не дотягивается, поэтому
 * ветка бесед регистрирует здесь отправщика: вернул `true` — реплика ушла,
 * `false` — доставить эту реплику ему некому (сокет чужой беседы или не готов),
 * оставляем в очереди до открытия нужной беседы.
 *
 * Пока отправщик не зарегистрирован, реплики просто ждут — не теряются.
 */
export function registerReplySender(sender: ReplySender | null): () => void {
  replySender = sender
  // Отправщик появился — самое время попробовать отложенные реплики: именно
  // это событие, а не таймер, расшивает «отложенные» (`parked`) элементы.
  if (sender) void flushQueue()
  return () => {
    if (replySender === sender) replySender = null
  }
}

/** Выполнить одно действие. См. `RunResult`. */
async function run(item: QueuedItem): Promise<RunResult> {
  const action = item.action
  if (action.kind === 'read') {
    // Ответ сервера — единственный источник правды про непрочитанные: он знает
    // и про записи за пределами выдачи ленты. Экран пометил точки оптимистично,
    // здесь бейдж сходится с сервером (M7.1).
    const res = await notesApi.markRead(action.paths)
    useAppStore.getState().setUnread(res.unread)
    return 'done'
  }
  if (action.kind === 'note') {
    await notesApi.create({ path: action.path, title: action.title, type: action.type })
    return 'done'
  }
  // Реплику доставляет открытая беседа, а не сеть. Нет отправщика или он
  // отвечает за другую беседу — это не сбой, а «пока некому»: ждём открытия.
  if (!replySender) return 'parked'
  return (await replySender(action.sessionId, action.text)) ? 'done' : 'parked'
}

/**
 * Разобрать очередь. По одному и по порядку: «создать заметку» и «отметить её
 * прочитанной» переставлять местами нельзя.
 *
 * Единственное исключение — отложенные (`parked`) реплики: их доставляет сокет
 * открытой беседы, от заметок и отметок они не зависят вообще, поэтому такой
 * элемент не имеет права держать всю очередь. Его перешагиваем и идём дальше;
 * в очереди он остаётся и уедет, когда беседу откроют.
 */
export async function flushQueue(): Promise<void> {
  start()
  ensureProfile()
  if (flushing) return
  if (typeof navigator !== 'undefined' && !navigator.onLine) return
  if (queue.length === 0) return

  flushing = true
  try {
    let index = 0
    while (index < queue.length) {
      const item = queue[index]
      inflight = item
      let result: RunResult
      try {
        result = await run(item)
      } catch (error) {
        // 4xx — сервер понял запрос и отказал: заметка с таким путём уже есть,
        // токен протух, путь исчез. Повтор ничего не изменит, а очередь встанет
        // навсегда и счётчик будет врать. Такое выкидываем; сетевые ошибки
        // (fetch кинул, 5xx, таймаут) оставляем и повторяем.
        if (!isPermanent(error)) {
          scheduleRetry()
          return
        }
        result = 'done'
      } finally {
        inflight = null
      }
      if (result === 'retry') {
        scheduleRetry()
        return
      }
      if (result === 'parked') {
        // Таймер не заводим: ждать нечего, ждём события (открытие беседы).
        index += 1
        continue
      }
      // Очередь могли дописать, пока элемент уезжал, поэтому ищем его заново.
      const at = queue.indexOf(item)
      if (at !== -1) queue.splice(at, 1)
      persist()
      refresh(true)
    }
    // Сюда попадаем, только если ни один повтор не понадобился: до сервера
    // доехало всё, что до него вообще шло. Значит и счётчик попыток пора
    // обнулить, и таймер бэкоффа больше никого не ждёт.
    attempt = 0
    // А вот «синхронизировано только что» — про факт обмена с сервером.
    // Очередь из одних отложенных реплик обмена не делала: не врём.
    if (queue.length === 0) markSynced()
  } finally {
    flushing = false
    inflight = null
  }
}

function isPermanent(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const status = (error as { status?: unknown }).status
  // ApiError из api.ts; 408 «сервер не ответил» — это таймаут, он повторяемый.
  return typeof status === 'number' && status >= 400 && status < 500 && status !== 408
}

function scheduleRetry(): void {
  if (retryTimer !== null) return
  const delay = RETRY_MS[Math.min(attempt, RETRY_MS.length - 1)]
  attempt += 1
  retryTimer = window.setTimeout(() => {
    retryTimer = null
    void flushQueue()
  }, delay)
}

// ---------- запуск ----------

function start(): void {
  if (started || typeof window === 'undefined') return
  started = true

  const onOnline = () => {
    attempt = 0
    refresh(true)
    void flushQueue()
  }
  window.addEventListener('online', onOnline)
  window.addEventListener('offline', () => refresh(true))
  // Телефон засыпает вместе с вкладкой: события `online` во сне может и не
  // случиться, а вернувшись, человек видит счётчик и ждёт, что тот растает.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void flushQueue()
  })

  // «синхронизировано N минут назад» берём из факта, а не из намерения: дерево
  // приехало с сервера без ошибки — значит связь есть и база свежая. Отдельного
  // вызова из экранов для этого не нужно, но `markSynced()` открыт наружу для
  // случаев, когда экран знает больше (например, дотянул ленту).
  let lastTree = useAppStore.getState().tree
  useAppStore.subscribe((s) => {
    if (s.tree === lastTree || s.treeError !== null) return
    lastTree = s.tree
    markSynced()
  })

  ensureProfile()
  refresh(false)
  void flushQueue()
}

// ---------- пуш-уведомления (M10.6) ----------

/**
 * Пушей пока нет и делать вид, что есть, нельзя.
 *
 * Чего не хватает на сервере: VAPID-ключей, ручки подписки
 * (`POST /api/push/subscribe`), хранения подписок рядом с профилем и отправки
 * web-push из места, где сессия помечается завершённой. Ничего этого в
 * `server/agentbox/api/` нет — есть только WebSocket, живущий, пока открыта
 * вкладка. Поэтому интерфейс показывает текст с бейджем «скоро», а не кнопку
 * (правило SPEC: незаконченное — не кнопка).
 */
export const PUSH_READY = false

/** Тихие часы по умолчанию: с 23:00 до 08:00 не будим. */
export const QUIET_HOURS = { from: 23, to: 8 }

/** Попадает ли момент в тихие часы. Пригодится, когда пуши включат. */
export function isQuietHour(at: Date = new Date()): boolean {
  const hour = at.getHours()
  const { from, to } = QUIET_HOURS
  // Интервал переходит через полночь, поэтому не `hour >= from && hour < to`.
  return from > to ? hour >= from || hour < to : hour >= from && hour < to
}
